import { useLocation, useParams, useRouter } from "@tanstack/react-router";
import { createContext, useContext, useMemo } from "react";

export interface PostModalParams {
  postSlug: string;
  spaceSlug: string;
}

interface PostModalState {
  postModal?: PostModalParams;
}

// The modal target post is stored in location.state.postModal on navigation.
// When the modal opens over another post page, the background route still
// points at that page. Wrap the overlay with PostModalOverlayProvider so
// usePostModalParams reads the modal target and overlay-only hooks react to the masked URL instead of the background route.
const PostModalOverlayContext = createContext({
  isPostModalOverlay: false,
});
PostModalOverlayContext.displayName = "PostModalOverlayContext";

export const PostModalOverlayProvider = PostModalOverlayContext.Provider;

export const useIsPostModalOverlay = () =>
  useContext(PostModalOverlayContext).isPostModalOverlay;

export const usePostModalParams = () => {
  const isPostModalOverlay = useIsPostModalOverlay();

  const routeParams: PostModalParams | undefined = useParams({
    from: "/_protected/_dynamicLayout/c/$spaceSlug/$postSlug",
    shouldThrow: false,
  });

  const locationStateParams = useLocation({
    select: location => (location.state as PostModalState)?.postModal,
  });

  if (isPostModalOverlay && locationStateParams) {
    return locationStateParams;
  }

  return {
    postSlug: routeParams?.postSlug ?? locationStateParams?.postSlug,
    spaceSlug: routeParams?.spaceSlug ?? locationStateParams?.spaceSlug,
  };
};

const normalizePathname = (pathname: string) =>
  pathname.split("#")[0].split("?")[0];

export const usePostModalParamsFromPathname = (
  pathname?: string | null,
): PostModalParams | undefined => {
  const router = useRouter();

  return useMemo(() => {
    if (!pathname) return undefined;

    const match = router
      .matchRoutes(normalizePathname(pathname))
      .find(({ fullPath }) => fullPath === "/c/$spaceSlug/$postSlug");

    const params = match?.params as PostModalParams | undefined;
    const { spaceSlug, postSlug } = params ?? {};

    if (typeof spaceSlug !== "string" || typeof postSlug !== "string") {
      return undefined;
    }

    return { spaceSlug, postSlug };
  }, [pathname, router]);
};
